import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Mail, Truck, CreditCard, Gift } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [smtp, setSmtp] = useState({ host: '', port: '587', username: '', password: '', from_email: '' });
  const [shiprocket, setShiprocket] = useState({ email: '', password: '', pickup_pincode: '', pickup_location: '' });
  const [razorpay, setRazorpay] = useState({ key_id: '', key_secret: '' });
  const [promo, setPromo] = useState({ enabled: false, heading: '', message: '', coupon_code: '', discount_text: '' });
  const [delivery, setDelivery] = useState({ free_threshold: '500', charge: '60', cod_enabled: false, cod_max_amount: '5000', cod_convenience_fee: '30' });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/admin/settings/smtp`, { withCredentials: true }),
      axios.get(`${API}/api/admin/settings/shiprocket`, { withCredentials: true }),
      axios.get(`${API}/api/admin/settings/razorpay`, { withCredentials: true }),
      axios.get(`${API}/api/admin/settings/promo`, { withCredentials: true }),
      axios.get(`${API}/api/admin/settings/delivery`, { withCredentials: true }),
    ]).then(([s, sh, r, p, d]) => {
      if (s.data.value && Object.keys(s.data.value).length) {
        const sv = { ...smtp, ...s.data.value };
        sv.password = '';
        setSmtp(sv);
      }
      if (sh.data.value && Object.keys(sh.data.value).length) {
        // Keep the masked '••••••••' from the API so the form can show
        // a "✓ Password saved" indicator. The save endpoint preserves the
        // existing password if the form value is masked or empty.
        setShiprocket({ ...shiprocket, ...sh.data.value });
      }
      if (r.data.value && Object.keys(r.data.value).length) {
        const rv = { ...razorpay, ...r.data.value };
        rv.key_secret = '';
        setRazorpay(rv);
      }
      if (p.data.value && Object.keys(p.data.value).length) {
        setPromo({ ...promo, ...p.data.value });
      }
      if (d.data.value && Object.keys(d.data.value).length) {
        setDelivery({
          free_threshold: (d.data.value.free_threshold || 500).toString(),
          charge: (d.data.value.charge || 60).toString(),
          cod_enabled: !!d.data.value.cod_enabled,
          cod_max_amount: (d.data.value.cod_max_amount ?? 5000).toString(),
          cod_convenience_fee: (d.data.value.cod_convenience_fee ?? 30).toString(),
        });
      }
    }).catch(() => {});
  }, []);

  const saveSmtp = async () => {
    try {
      await axios.put(`${API}/api/admin/settings/smtp`, { value: smtp }, { withCredentials: true });
      toast.success('SMTP settings saved');
    } catch { toast.error('Failed to save'); }
  };

  const testEmail = async () => {
    try {
      const { data } = await axios.post(`${API}/api/admin/test-email`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Test email failed'); }
  };

  const saveShiprocket = async () => {
    try {
      await axios.put(`${API}/api/admin/settings/shiprocket`, { value: shiprocket }, { withCredentials: true });
      toast.success('Shiprocket settings saved. Token will auto-generate on next API call.');
    } catch { toast.error('Failed to save'); }
  };

  const saveRazorpay = async () => {
    try {
      await axios.put(`${API}/api/admin/settings/razorpay`, { value: razorpay }, { withCredentials: true });
      toast.success('Razorpay keys saved');
    } catch { toast.error('Failed to save'); }
  };

  const savePromo = async () => {
    try {
      await axios.put(`${API}/api/admin/settings/promo`, { value: promo }, { withCredentials: true });
      toast.success('Promo popup settings saved');
    } catch { toast.error('Failed to save'); }
  };

  const saveDelivery = async () => {
    try {
      await axios.put(`${API}/api/admin/settings/delivery`, { value: {
        free_threshold: parseFloat(delivery.free_threshold) || 500,
        charge: parseFloat(delivery.charge) || 60,
        cod_enabled: !!delivery.cod_enabled,
        cod_max_amount: parseFloat(delivery.cod_max_amount) || 0,
        cod_convenience_fee: parseFloat(delivery.cod_convenience_fee) || 0,
      } }, { withCredentials: true });
      toast.success('Delivery settings saved');
    } catch { toast.error('Failed to save'); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16 max-w-2xl">
        <h1 className="text-3xl font-black font-heading mb-8" data-testid="admin-settings-heading">Platform Settings</h1>

        <Tabs defaultValue="smtp" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="smtp"><Mail className="h-4 w-4 mr-1.5" /> Email</TabsTrigger>
            <TabsTrigger value="shiprocket"><Truck className="h-4 w-4 mr-1.5" /> Shiprocket</TabsTrigger>
            <TabsTrigger value="razorpay"><CreditCard className="h-4 w-4 mr-1.5" /> Razorpay</TabsTrigger>
            <TabsTrigger value="promo"><Gift className="h-4 w-4 mr-1.5" /> Promo</TabsTrigger>
            <TabsTrigger value="delivery"><Truck className="h-4 w-4 mr-1.5" /> Delivery</TabsTrigger>
          </TabsList>

          <TabsContent value="smtp">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="font-heading font-semibold text-lg">SMTP Email Settings</h2>
              <p className="text-xs text-muted-foreground">Configure SMTP to send order confirmation, shipping, and admin alert emails.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>SMTP Host</Label><Input value={smtp.host} onChange={(e) => setSmtp({...smtp, host: e.target.value})} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
                <div><Label>Port</Label><Input value={smtp.port} onChange={(e) => setSmtp({...smtp, port: e.target.value})} placeholder="587" data-testid="smtp-port" /></div>
              </div>
              <div><Label>Username / Email</Label><Input value={smtp.username} onChange={(e) => setSmtp({...smtp, username: e.target.value})} placeholder="your@gmail.com" data-testid="smtp-username" /></div>
              <div><Label>Password / App Password</Label><Input type="password" value={smtp.password} onChange={(e) => setSmtp({...smtp, password: e.target.value})} placeholder="Enter app password (leave empty to keep existing)" data-testid="smtp-password" /><p className="text-[10px] text-muted-foreground mt-1">Leave empty to keep current password unchanged</p></div>
              <div><Label>From Email</Label><Input value={smtp.from_email} onChange={(e) => setSmtp({...smtp, from_email: e.target.value})} placeholder="noreply@petrichor.in" data-testid="smtp-from" /></div>
              <div className="flex gap-2">
                <Button onClick={saveSmtp} data-testid="save-smtp-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
                <Button variant="outline" onClick={testEmail} data-testid="test-email-button">Send Test Email</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shiprocket">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="font-heading font-semibold text-lg">Shiprocket Credentials</h2>
              <p className="text-xs text-muted-foreground">Enter your Shiprocket login. Token is auto-generated and refreshed (no static token needed).</p>
              <div><Label>Shiprocket Email</Label><Input value={shiprocket.email} onChange={(e) => setShiprocket({...shiprocket, email: e.target.value})} placeholder="your@shiprocket.com" data-testid="shiprocket-email" /></div>
              <div>
                <Label>Shiprocket Password</Label>
                <Input
                  type="password"
                  value={shiprocket.password === '••••••••' ? '' : shiprocket.password}
                  onChange={(e) => setShiprocket({...shiprocket, password: e.target.value})}
                  placeholder={shiprocket.password === '••••••••' ? 'Leave empty to keep saved password' : 'Enter password'}
                  data-testid="shiprocket-password"
                />
                {shiprocket.password === '••••••••' ? (
                  <p className="text-xs text-green-700 mt-1 font-medium" data-testid="shiprocket-password-saved">
                    ✓ Password is saved. Leave the field empty to keep it; type a new one only if you want to change it.
                  </p>
                ) : (
                  <p className="text-xs text-amber-700 mt-1" data-testid="shiprocket-password-warning">
                    ⚠ Type your Shiprocket panel password.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Pincode</Label>
                  <Input
                    value={shiprocket.pickup_pincode || ''}
                    onChange={(e) => setShiprocket({...shiprocket, pickup_pincode: e.target.value})}
                    placeholder="e.g. 415002"
                    data-testid="shiprocket-pickup-pincode"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Origin PIN of your warehouse. Used to fetch the right courier list and rates.</p>
                </div>
                <div>
                  <Label>Pickup Location Nickname</Label>
                  <Input
                    value={shiprocket.pickup_location || ''}
                    onChange={(e) => setShiprocket({...shiprocket, pickup_location: e.target.value})}
                    placeholder="e.g. Primary"
                    data-testid="shiprocket-pickup-location"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must match the pickup location nickname configured in your Shiprocket panel.</p>
                </div>
              </div>
              <Button onClick={saveShiprocket} data-testid="save-shiprocket-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
              <Button variant="outline" className="ml-2" onClick={async () => {
                try {
                  const { data } = await axios.post(`${API}/api/admin/settings/shiprocket/test`, {}, { withCredentials: true });
                  if (data.ok) toast.success(data.message);
                  else toast.error(data.message || 'Test failed');
                } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
              }} data-testid="test-shiprocket-button">Test Connection</Button>
            </div>
          </TabsContent>

          <TabsContent value="razorpay">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="font-heading font-semibold text-lg">Razorpay Keys</h2>
              <p className="text-xs text-muted-foreground">Currently using test keys. Replace with live keys when ready for production.</p>
              <div><Label>Key ID</Label><Input value={razorpay.key_id} onChange={(e) => setRazorpay({...razorpay, key_id: e.target.value})} placeholder="rzp_live_xxxxx" data-testid="razorpay-key-id" /></div>
              <div><Label>Key Secret</Label><Input type="password" value={razorpay.key_secret} onChange={(e) => setRazorpay({...razorpay, key_secret: e.target.value})} placeholder="Secret key" data-testid="razorpay-key-secret" /></div>
              <Button onClick={saveRazorpay} data-testid="save-razorpay-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
              <p className="text-xs text-muted-foreground">After saving live keys, all future checkouts will use live Razorpay processing with signature verification.</p>
            </div>
          </TabsContent>

          <TabsContent value="promo">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="font-heading font-semibold text-lg">Promotional Popup</h2>
              <p className="text-xs text-muted-foreground">Shows once per visitor on first visit. Stored in localStorage so it won't repeat.</p>
              <div className="flex items-center gap-3">
                <Switch checked={promo.enabled} onCheckedChange={v => setPromo({...promo, enabled: v})} data-testid="promo-enabled-switch" />
                <Label>Enable Popup</Label>
              </div>
              <div><Label>Heading</Label><Input value={promo.heading} onChange={e => setPromo({...promo, heading: e.target.value})} placeholder="Welcome to Rejumist™!" data-testid="promo-heading" /></div>
              <div><Label>Message</Label><Input value={promo.message} onChange={e => setPromo({...promo, message: e.target.value})} placeholder="Use code below for 15% off your first order" data-testid="promo-message" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Coupon Code</Label><Input value={promo.coupon_code} onChange={e => setPromo({...promo, coupon_code: e.target.value.toUpperCase()})} placeholder="WELCOME15" data-testid="promo-coupon-code" /></div>
                <div><Label>Discount Text (badge)</Label><Input value={promo.discount_text} onChange={e => setPromo({...promo, discount_text: e.target.value})} placeholder="15% Off" data-testid="promo-discount-text" /></div>
              </div>
              <Button onClick={savePromo} data-testid="save-promo-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
            </div>
          </TabsContent>

          <TabsContent value="delivery">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="font-heading font-semibold text-lg">Delivery Charges</h2>
              <p className="text-xs text-muted-foreground">Set the free delivery threshold and delivery charge. These values are used on product pages and in the cart.</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Free Delivery Threshold ({'\u20B9'})</Label><Input type="number" value={delivery.free_threshold} onChange={e => setDelivery({...delivery, free_threshold: e.target.value})} placeholder="500" data-testid="delivery-threshold" /></div>
                <div><Label>Delivery Charge ({'\u20B9'})</Label><Input type="number" value={delivery.charge} onChange={e => setDelivery({...delivery, charge: e.target.value})} placeholder="60" data-testid="delivery-charge" /></div>
              </div>

              <div className="pt-4 mt-2 border-t border-[#E0E0E0]">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-heading font-semibold">Cash on Delivery</h3>
                    <p className="text-xs text-muted-foreground">Available only to logged-in users at checkout.</p>
                  </div>
                  <Switch checked={!!delivery.cod_enabled} onCheckedChange={v => setDelivery({...delivery, cod_enabled: v})} data-testid="cod-enabled-switch" />
                </div>
                {delivery.cod_enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Max order amount for COD ({'\u20B9'})</Label>
                      <Input type="number" value={delivery.cod_max_amount} onChange={e => setDelivery({...delivery, cod_max_amount: e.target.value})} placeholder="5000" data-testid="cod-max-amount" />
                      <p className="text-xs text-muted-foreground mt-1">0 = no cap</p>
                    </div>
                    <div>
                      <Label>Convenience fee ({'\u20B9'})</Label>
                      <Input type="number" value={delivery.cod_convenience_fee} onChange={e => setDelivery({...delivery, cod_convenience_fee: e.target.value})} placeholder="30" data-testid="cod-convenience-fee" />
                      <p className="text-xs text-muted-foreground mt-1">Added to the total at checkout.</p>
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={saveDelivery} data-testid="save-delivery-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettings;
